import { isGoogleAdsTokenExpired, useGoogleAdsOAuthStore } from "../hooks/use-google-ads-oauth-store";

const GOOGLE_ADS_PROXY_URL = "/api/v1/google-ads-proxy";
export const GOOGLE_ADS_API_VERSION_PATH = "/v24";

interface IGoogleAdsProxyRequest {
    method: "GET" | "POST";
    path: string;
    body?: unknown;
    loginCustomerId?: string;
}

interface IGoogleAdsProxyErrorPayload {
    error?: string | { message?: string; status?: string };
}

const isGoogleUnauthenticated = (payload: IGoogleAdsProxyErrorPayload | undefined): boolean =>
    typeof payload?.error === "object" && payload.error.status === "UNAUTHENTICATED";

export const callGoogleAdsProxy = async <T>(request: IGoogleAdsProxyRequest): Promise<T> => {
    // The only place the token is read, at send time.
    const authState = useGoogleAdsOAuthStore.getState();
    const { accessToken } = authState;
    if (!accessToken || isGoogleAdsTokenExpired(authState)) {
        throw new Error("Not connected to Google Ads.");
    }

    const response = await fetch(GOOGLE_ADS_PROXY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // In the body, not a header: Pro's cookie auth forwards any Bearer `Authorization` header to its own
        // JWT scheme, which rejects the Google token before the proxy runs.
        body: JSON.stringify({ ...request, accessToken: `Bearer ${accessToken}` }),
    });

    const responseText = await response.text();
    let payload: (T & IGoogleAdsProxyErrorPayload) | undefined;
    try {
        payload = responseText ? (JSON.parse(responseText) as T & IGoogleAdsProxyErrorPayload) : undefined;
    } catch {
        throw new Error(
            `Google Ads proxy returned a non-JSON response (${response.status}): ${responseText.slice(0, 200)}`,
        );
    }

    // A 401 also comes from Pro's own auth and from the proxy itself (missing header). Only Google's
    // UNAUTHENTICATED body means the Google token is bad; anything else must keep its own message.
    if (response.status === 401 && isGoogleUnauthenticated(payload)) {
        useGoogleAdsOAuthStore.getState().reset();
        throw new Error("Your Google Ads connection expired. Reconnect to continue.");
    }

    if (!response.ok) {
        const error = payload?.error;
        const message = typeof error === "string" ? error : error?.message;
        throw new Error(message ?? `Google Ads proxy request failed (${response.status})`);
    }

    if (!payload) {
        throw new Error("Google Ads proxy returned an empty response body.");
    }

    return payload;
};

// v1 (SIM-111) only ever creates KEYWORD ad-group criteria, so BROAD is the sole supported
// match type — see SIM-104 master plan. Widened only if a future ticket needs EXACT/PHRASE.
export type IGoogleAdsKeywordMatchType = "BROAD";

export interface IGoogleAdsAdGroupCriterionCreateOperation {
    create: {
        adGroup: string;
        negative: boolean;
        keyword: { text: string; matchType: IGoogleAdsKeywordMatchType };
        // Google Ads rejects `status` on negative criteria in v24 — only set it for
        // non-negative (enabled) creates. Omitted entirely (not `undefined`-valued) when negative.
        status?: "ENABLED";
    };
}

// Removing a criterion (SIM-113) is keyed only by its resource name — e.g.
// "customers/123/adGroupCriteria/456~789". The criterion id isn't stored anywhere client-side,
// so callers resolve it first via a googleAds:search on the ad group (see
// use-google-ads-remove-keywords).
export interface IGoogleAdsAdGroupCriterionRemoveOperation {
    remove: string;
}

export type IGoogleAdsAdGroupCriterionOperation =
    | IGoogleAdsAdGroupCriterionCreateOperation
    | IGoogleAdsAdGroupCriterionRemoveOperation;

export interface IMutateAdGroupCriteriaRequest {
    customerId: string;
    loginCustomerId?: string;
    operations: IGoogleAdsAdGroupCriterionOperation[];
}

export interface IGoogleAdsMutateResult {
    resourceName?: string;
}

interface IGoogleAdsErrorLocationFieldPathElement {
    fieldName?: string;
    index?: number;
}

interface IGoogleAdsFailureError {
    errorCode?: Record<string, string>;
    message?: string;
    location?: { fieldPathElements?: IGoogleAdsErrorLocationFieldPathElement[] };
}

interface IGoogleAdsFailureDetail {
    "@type"?: string;
    errors?: IGoogleAdsFailureError[];
}

interface IGoogleAdsPartialFailureError {
    code?: number;
    message?: string;
    details?: IGoogleAdsFailureDetail[];
}

export interface IMutateAdGroupCriteriaResponse {
    results?: IGoogleAdsMutateResult[];
    partialFailureError?: IGoogleAdsPartialFailureError;
}

export const mutateAdGroupCriteria = async ({
    customerId,
    loginCustomerId,
    operations,
}: IMutateAdGroupCriteriaRequest): Promise<IMutateAdGroupCriteriaResponse> => {
    return callGoogleAdsProxy<IMutateAdGroupCriteriaResponse>({
        method: "POST",
        path: `${GOOGLE_ADS_API_VERSION_PATH}/customers/${customerId}/adGroupCriteria:mutate`,
        // partialFailure lets duplicate/invalid keywords fail individually instead of the whole
        // batch — required so the per-keyword parser below can report success alongside errors.
        body: { operations, partialFailure: true },
        loginCustomerId,
    });
};

export type GoogleAdsKeywordMutateStatus = "success" | "duplicate" | "error";

export interface IGoogleAdsKeywordMutateResult {
    keyword: string;
    status: GoogleAdsKeywordMutateStatus;
    message?: string;
    // Set when a remove found no matching criterion: Google Ads already doesn't have the keyword.
    // It still reports as an error, but the keyword is in the state the remove was aiming for.
    alreadyAbsent?: boolean;
}

const GOOGLE_ADS_FAILURE_DETAIL_TYPE_MARKER = "GoogleAdsFailure";
// Google Ads error-code enum values for an already-existing criterion are consistently named
// with "ALREADY_EXISTS" or "DUPLICATE" (e.g. `criterionError: "AD_GROUP_CRITERION_ALREADY_EXISTS"`),
// regardless of which specific error-code family (criterionError, adGroupCriterionError, ...)
// carries it. Matching on the substring — rather than one hardcoded enum literal — keeps this
// robust to which exact enum Google Ads returns for a given duplicate.
const DUPLICATE_CRITERION_ERROR_PATTERN = /ALREADY_EXISTS|DUPLICATE/u;
// Google Ads keys a keyword AdGroupCriterion by (ad group, keyword text, match type) — the
// `negative` flag isn't part of that identity. So creating a keyword as negative when it already
// exists (non-negative) in the same ad group doesn't come back as ALREADY_EXISTS/DUPLICATE: it
// resolves to the same existing criterion and fails with IMMUTABLE_FIELD on the one field that
// differs (`negative`), since that field can't be changed after creation. Treated as a duplicate
// for the user's purposes — the keyword is already active in this ad group either way.
const IMMUTABLE_FIELD_ERROR_PATTERN = /IMMUTABLE_FIELD/u;
const NEGATIVE_FIELD_NAME = "negative";

/**
 * Maps a batched adGroupCriteria:mutate response (called with `partialFailure: true`) back onto
 * one structured result per input keyword, preserving input order (SIM-111).
 *
 * Per Google Ads partial-failure semantics, `results[]` omits failed operations entirely but
 * keeps the relative order of the successful ones — it is NOT index-aligned with `operations[]`.
 * Failed operations are instead reported in `partialFailureError.details[]` (a `GoogleAdsFailure`),
 * each carrying the failing operation's index via `location.fieldPathElements`. So: pull the
 * failing indices out of the error details first, then walk the keywords in order, consuming
 * `results[]` in sequence for every index that wasn't reported as a failure.
 */
export const parseAdGroupCriteriaMutateResults = (
    response: IMutateAdGroupCriteriaResponse,
    keywords: string[],
): IGoogleAdsKeywordMutateResult[] => {
    const failuresByOperationIndex = new Map<number, { status: "duplicate" | "error"; message?: string }>();

    const failureDetails = (response.partialFailureError?.details ?? []).filter((detail) =>
        (detail["@type"] ?? "").includes(GOOGLE_ADS_FAILURE_DETAIL_TYPE_MARKER),
    );

    for (const detail of failureDetails) {
        for (const error of detail.errors ?? []) {
            const operationIndex = error.location?.fieldPathElements?.find(
                (element) => element.fieldName === "operations",
            )?.index;
            if (operationIndex === undefined) {
                continue;
            }

            const errorCodeValues = Object.values(error.errorCode ?? {});
            const isImmutableNegativeFieldCollision =
                errorCodeValues.some((code) => IMMUTABLE_FIELD_ERROR_PATTERN.test(code)) &&
                (error.location?.fieldPathElements ?? []).some((element) => element.fieldName === NEGATIVE_FIELD_NAME);
            const isDuplicate =
                errorCodeValues.some((code) => DUPLICATE_CRITERION_ERROR_PATTERN.test(code)) ||
                isImmutableNegativeFieldCollision;

            failuresByOperationIndex.set(operationIndex, {
                status: isDuplicate ? "duplicate" : "error",
                message: error.message,
            });
        }
    }

    const remainingSuccessResults = [...(response.results ?? [])];

    return keywords.map((keyword, operationIndex) => {
        const failure = failuresByOperationIndex.get(operationIndex);
        if (failure) {
            return { keyword, status: failure.status, message: failure.message };
        }

        const matchedResult = remainingSuccessResults.shift();
        if (!matchedResult) {
            return {
                keyword,
                status: "error",
                message: "Google Ads did not return a result for this keyword.",
            };
        }

        return { keyword, status: "success" };
    });
};
